"use client";
import { useTranslations } from "next-intl"

import { useState } from "react";
import { DockSheet } from "@/components/ui/dock-sheet";
import { DialogCard } from "@/components/ui/dialog-card";
import { Button } from "@/components/ui/button";
import { Bell, User as UserIcon } from "lucide-react";
import Image from "next/image";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { SQUARE_SM } from "@/lib/images/presets";
import { useMobile } from "@/hooks/use-mobile";

interface ProfileNotificationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  profile: {
    id: string;
    username: string;
    avatar_url?: string | null;
  };
  mode?: "enable" | "disable" | "unfollow";
}

export function ProfileNotificationModal({
  open,
  onClose,
  onConfirm,
  profile,
  mode = "enable",
}: ProfileNotificationModalProps) {
  const t = useTranslations("profile.notifications");
  const [imgError, setImgError] = useState(false);
  const isMobile = useMobile();

  // `h-16 w-16` = 64 CSS px -> SQUARE_SM, same as `AuthModal`'s hero so the
  // two modals share one cache entry for the same person.
  const avatarUrl = getAvatarUrl(profile.avatar_url, SQUARE_SM);
  const avatar = avatarUrl && !imgError ? (
    <Image
      src={avatarUrl}
      alt={profile.username || "User"}
      fill
      // Same as `AuthModal`: the src is already a 192 device px CDN crop, so
      // `unoptimized` skips a second billed transformation — and no `sizes`,
      // because `unoptimized` drops both `srcSet` and `sizes` before they reach
      // the DOM (see the longer note in `auth-modal.tsx`).
      unoptimized
      className="object-cover"
      onError={() => setImgError(true)}
    />
  ) : (
    <UserIcon className="h-8 w-8 text-muted-foreground" />
  );

  const title =
    mode === "unfollow"
      ? t("unfollowTitle")
      : mode === "disable"
        ? t("disableTitle")
        : t("enableTitle");

  const description =
    mode === "unfollow"
      ? t("unfollowDesc", { username: profile.username })
      : mode === "disable"
        ? t("disableDesc", { username: profile.username })
        : t("enableDesc", { username: profile.username });

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  // Chrome-free by construction: the close control belongs to whichever
  // primitive renders this body (`DialogCard` on desktop, `DockSheet` on
  // mobile). Drawing it INSIDE this shared body is exactly what forced the
  // sheet into `chrome="none"` — HANDOFF_DIALOG_CHROME §3.
  const content = (
    <div className="flex flex-col items-center gap-6">
      <div className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-foreground/10 bg-foreground/10">
        {avatar}
      </div>

      <div className="space-y-2 text-center">
        <h2 className="text-xl font-semibold text-foreground flex items-center justify-center gap-2">
          <Bell className="h-5 w-5" />
          {title}
        </h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="w-full space-y-3">
        <Button
          className="w-full rounded-full"
          variant={mode === "disable" || mode === "unfollow" ? "outline" : "default"}
          size="lg"
          onClick={handleConfirm}
        >
          {mode === "unfollow" ? (
            t("unfollowTitle")
          ) : mode === "disable" ? (
            t("disableTitle")
          ) : (
            <>
              <Bell className="h-4 w-4 mr-2" />
              {t("enableTitle")}
            </>
          )}
        </Button>
      </div>
    </div>
  );

  if (!open) return null;

  if (isMobile) {
    return (
      <DockSheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
        // The body opens with its own hero (avatar + `text-xl` bell heading),
        // so a chrome title would be a second one — handle + close only.
        // Mirror of the desktop `chrome="closeOnly"` below.
        chrome="handleOnly"
        contentSized
        zIndex={10060}
      >
        {content}
      </DockSheet>
    );
  }

  return (
    <DialogCard
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      chrome="closeOnly"
      a11yTitle={title}
      panelClassName="max-w-sm"
    >
      {content}
    </DialogCard>
  );
}
